import React from 'react';
import PropTypes from 'prop-types';
import { Discontinued, Fulfillment } from '@thd-olt-component-react/fulfillment';
import { AddToCart } from '@thd-olt-component-react/add-to-cart';
import { TrackOutOfStock } from '@thd-olt-component-react/email-signup';
import { useConfigService } from '@thd-nucleus/experience-context';
import { isMobile } from '../../util';
import { BuyboxActions } from './BuyboxActions';
import * as helper from '../../BuyboxHelper';
import { BuyboxLiteQuantity } from './BuyboxLiteQuantity';

const { arrayOf, bool, shape, func, string, number } = PropTypes;

const BuyboxLite = ({
  itemId,
  store,
  configurableItems,
  unavailable,
  covidAlert,
  isATCDisabled,
  channel,
  fulfillment,
  availabilityType,
  onFulfillmentChange,
  onDeliveryOptionsZipCodeChange,
  onATCClick,
  bopisHolidayTiming,
  hideInstantCheckout,
  hidePaypalCheckout,
  directData,
  configuratorData,
  quantityLimit,
  currentSelectedFulfillment,
  configurableProductDetails,
  availableQuantity,
  subscription,
  showQuantity,
  customRisMessage,
  risMessageEligible,
  isRisMessageEnabled,
  minimumOrderQuantity,
  onQuantityChange,
  hideProReferral,
  buyboxState,
  productInfo,
  onProductAddOnChange,
}) => {
  const enableSms = useConfigService('enableSmsForOOSnotifications') || false;
  const { storeId, storeZip } = store;
  const {
    zipCode, isShowATC, fulfillmentMethod, paypalEligible
  } = buyboxState;
  if (showQuantity) {
    return (
      <BuyboxLiteQuantity
        itemId={itemId}
        unavailable={unavailable}
        covidAlert={covidAlert}
        store={store}
        productInfo={productInfo}
        isATCDisabled={isATCDisabled}
        channel={channel}
        hideProReferral={hideProReferral}
        fulfillment={fulfillment}
        onFulfillmentChange={onFulfillmentChange}
        onDeliveryOptionsZipCodeChange={onDeliveryOptionsZipCodeChange}
        onATCClick={onATCClick}
        configuratorData={configuratorData}
        bopisHolidayTiming={bopisHolidayTiming}
        hideInstantCheckout={hideInstantCheckout}
        hidePaypalCheckout={hidePaypalCheckout}
        directData={directData}
        quantityLimit={quantityLimit}
        currentSelectedFulfillment={currentSelectedFulfillment}
        availableQuantity={availableQuantity}
        subscription={subscription}
        showQuantity={showQuantity}
        onQuantityChange={onQuantityChange}
        customRisMessage={customRisMessage}
        risMessageEligible={risMessageEligible}
        isRisMessageEnabled={isRisMessageEnabled}
        minimumOrderQuantity={minimumOrderQuantity}
        configurableProductDetails={configurableProductDetails}
        buyboxState={buyboxState}
        onProductAddOnChange={onProductAddOnChange}
      />
    );
  }

  if (isMobile(channel)) {
    const shippingTemplate = helper.renderShippingFulfillment({ fulfillment, availabilityType });
    const cart = configuratorData?.product?.configuration?.cart;
    return (
      <div
        // eslint-disable-next-line tailwindcss/no-arbitrary-value
        className="sui-grid sui-ml-[-10px] sui-mr-[-10px] sui-w-auto sui-bg-primary sui-mb-1"
        data-component="BuyboxLiteMobile"
      >
        <div className="sui-grid-cols-12">
          {shippingTemplate.showATC && (
            <div className="sui-p-2 sui-overflow-hidden">
              <AddToCart
                cartOptions={cart
                  || helper.getCartOptions({
                    configurableItems,
                    channel,
                    fulfillmentMethod: shippingTemplate.method,
                    alterBrowserHistory: true
                  })}
                cartReqParams={cart
                  || helper.getCartReqParams({
                    itemId,
                    configurableItems,
                    storeZip,
                    fulfillmentMethod: shippingTemplate.method,
                    quantity: 1,
                    zipCode,
                    storeId,
                    fulfillment
                  })}
                onClick={onATCClick}
                disabled={isATCDisabled}
              >
                Add to Cart
              </AddToCart>
            </div>
          )}
          {shippingTemplate.discontinued && (
            <Discontinued twoTileToggle />
          )}
          <div className="sui-p-2 sui-overflow-hidden">
            {shippingTemplate.template}
          </div>
          {shippingTemplate.oos && (
            <TrackOutOfStock itemId={itemId} enableSms={enableSms} />
          )}
        </div>
      </div>
    );
  }

  return (
    // eslint-disable-next-line tailwindcss/no-arbitrary-value
    <div className="sui-grid sui-ml-[-10px] sui-mr-[-10px] sui-w-auto" data-component="BuyboxLite">
      <div className="sui-grid-cols-12">
        <div className="sui-p-2 sui-bg-primary sui-overflow-hidden">
          <div>
            {!unavailable && (
              <div>
                <div>
                  <Fulfillment
                    itemId={itemId}
                    covidAlert={covidAlert}
                    onChange={onFulfillmentChange}
                    onDeliveryZipCodeChange={onDeliveryOptionsZipCodeChange}
                    bopisHolidayTiming={bopisHolidayTiming}
                    directData={directData}
                    isBuyBoxLite
                    configurableProductDetails={configurableProductDetails}
                    currentSelectedFulfillment={currentSelectedFulfillment}
                  />
                </div>
              </div>
            )}
          </div>
          {isShowATC && (
            <>
              {/* eslint-disable-next-line tailwindcss/no-arbitrary-value */}
              <div className="sui-mt-3 sui-max-w-[278px]">
                <BuyboxActions
                  alterBrowserHistory
                  itemId={itemId}
                  storeId={storeId}
                  storeZip={storeZip}
                  disableATC={isATCDisabled}
                  channel={channel}
                  zipCode={zipCode}
                  fulfillmentMethod={fulfillmentMethod}
                  fulfillment={fulfillment}
                  paypalEligible={paypalEligible}
                  onATCClick={onATCClick}
                  hideQuantity
                  hideInstantCheckout={hideInstantCheckout}
                  hidePaypalCheckout={hidePaypalCheckout}
                  configuratorData={configuratorData}
                  quantityLimit={quantityLimit}
                  availableQuantity={availableQuantity}
                  subscription={subscription}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

BuyboxLite.propTypes = {
  itemId: string,
  unavailable: bool,
  covidAlert: bool,
  bopisHolidayTiming: number,
  isATCDisabled: bool,
  channel: string,
  store: shape({
    storeZip: string,
    storeName: string,
    storeId: string,
  }),
  configurableItems: arrayOf(
    shape({
      itemId: string,
      isAnchor: bool
    })
  ),
  fulfillment: shape({
    backordered: bool
  }),
  availabilityType: shape({
    discontinued: bool,
    status: bool,
    buyable: bool,
    type: string
  }),
  onFulfillmentChange: func,
  onDeliveryOptionsZipCodeChange: func,
  onATCClick: func,
  hideInstantCheckout: bool,
  hidePaypalCheckout: bool,
  /* eslint-disable-next-line react/forbid-prop-types */
  directData: PropTypes.objectOf(PropTypes.any),
  // eslint-disable-next-line react/forbid-prop-types
  configuratorData: arrayOf(PropTypes.any),
  quantityLimit: number,
  currentSelectedFulfillment: string,
  configurableProductDetails: PropTypes.shape({
    isSpecialOrder: bool,
    configuratorHideQuantity: bool,
    /* eslint-disable-next-line react/forbid-prop-types */
    configuratorData: PropTypes.objectOf(PropTypes.any),
    isConfigurableBlinds: bool
  }),
  availableQuantity: shape({
    pickup: number,
    delivery: number
  }),
  subscription: shape({
    discountPercentage: string
  }),
  minimumOrderQuantity: number,
  onQuantityChange: func,
  risMessageEligible: bool,
  isRisMessageEnabled: bool,
  customRisMessage: string,
  showQuantity: bool,
  hideProReferral: bool,
  buyboxState: shape({
    disableATC: bool,
    paypalEligible: bool,
    quantity: number,
    coverage: number,
    zipCode: string,
    isShowATC: bool,
    isItemOOS: bool,
    fulfillmentMethod: string,
    /* eslint-disable-next-line react/forbid-prop-types */
    availableFulfillments: arrayOf(PropTypes.any),
    addAdditional: bool,
    quantityExceeded: bool,
    showFencingMessage: bool,
    minimumBopisQuantity: number,
    maximumBopisQuantity: number,
    isOutOfStockOnline: bool,
    quantityLimitExceed: bool,
  }),
  onProductAddOnChange: func,
  productInfo: shape({
    productDepartment: string,
    subClassNumber: string,
    classNumber: string,
  })
};

BuyboxLite.defaultProps = {
  itemId: null,
  configurableItems: null,
  unavailable: false,
  store: {
    storeId: null,
    storeZip: null,
  },
  covidAlert: false,
  isATCDisabled: false,
  channel: 'desktop',
  fulfillment: null,
  availabilityType: null,
  onFulfillmentChange: /* istanbul ignore next */ () => { },
  onDeliveryOptionsZipCodeChange: /* istanbul ignore next */ () => { },
  onATCClick: /* istanbul ignore next */ () => { },
  bopisHolidayTiming: null,
  hideInstantCheckout: false,
  hidePaypalCheckout: false,
  directData: null,
  configuratorData: null,
  quantityLimit: 0,
  currentSelectedFulfillment: '',
  configurableProductDetails: {
    isSpecialOrder: false,
    configuratorHideQuantity: false,
    configuratorData: {},
    isConfigurableBlinds: false
  },
  availableQuantity: null,
  subscription: null,
  onQuantityChange: () => { },
  minimumOrderQuantity: 1,
  showQuantity: false,
  risMessageEligible: false,
  isRisMessageEnabled: false,
  customRisMessage: '',
  hideProReferral: false,
  buyboxState: {
    quantity: 1,
    zipCode: null,
    isItemOOS: false,
    isShowATC: true,
    paypalEligible: false,
    isOutOfStockOnline: false,
    minimumBopisQuantity: null,
    maximumBopisQuantity: null,
    quantityLimitExceed: false,
    availableFulfillments: {}
  },
  onProductAddOnChange: () => { },
  productInfo: {
    productDepartment: '',
    subClassNumber: '',
    classNumber: '',
  }
};

export { BuyboxLite };
